/**
 * A single line of a voice call transcript.
 * `system` lines are call events (hold, resume, connection problems) and are not spoken by anyone.
 * The sender name is never stored: agent lines show the logged-in agent and customer lines show the contact's customer.
 * The text normally contains no names. For older saved transcripts, the placeholders agentName, customerName
 * and customerFirstName in double braces are still filled in when the transcript is displayed.
 */
export interface TranscriptMessage {
  messageFrom: 'agent' | 'customer' | 'system';
  message: string;
  /** Display time, e.g. '9:10 AM' */
  time?: string;
  /** Icon for `system` lines; 'custom' shows the uploaded image in iconUrl */
  eventType?: 'hold' | 'resume' | 'error' | 'success' | 'custom';
  /** Custom event icon as a small image data URL, used when eventType is 'custom' */
  iconUrl?: string;
}

/**
 * Mock transcript between an agent and a customer.
 * It is the default for every voice contact; a contact can override it with its own `transcriptData`
 * (customised from Simulator-v2).
 */
export const INITIAL_TRANSCRIPT: TranscriptMessage[] = [
  {
    messageFrom: 'agent',
    time: '9:10 AM',
    message:
      'Thank you for calling Delta Customer Service. How can I help you today?',
  },
  {
    messageFrom: 'customer',
    time: '9:10 AM',
    message: 'Hi, I am calling about my damaged luggage.',
  },
  {
    messageFrom: 'agent',
    time: '9:10 AM',
    message:
      'I am sorry to hear that. Could you share the flight number and the date you travelled so I can pull up your booking?',
  },
  {
    messageFrom: 'customer',
    time: '9:10 AM',
    message:
      'It was flight DL 482 from Atlanta to Salt Lake City, last Tuesday. The handle is broken and one wheel is missing.',
  },
  { messageFrom: 'system', eventType: 'hold', time: '9:11 AM', message: 'Call on hold' },
  { messageFrom: 'system', eventType: 'resume', time: '9:12 AM', message: 'Call resumed' },
  {
    messageFrom: 'agent',
    time: '9:12 AM',
    message:
      'Thank you for waiting. I found your booking and I can see the baggage report was filed at the airport.',
  },
  {
    messageFrom: 'customer',
    time: '9:12 AM',
    message:
      'Yes, I filed it right after landing. Will I be compensated for the damage?',
  },
  {
    messageFrom: 'system',
    eventType: 'error',
    time: '9:14 AM',
    message: 'Poor Connection - transcript stopped',
  },
  {
    messageFrom: 'system',
    eventType: 'success',
    time: '9:14 AM',
    message: 'Connection restored',
  },
  {
    messageFrom: 'agent',
    time: '9:15 AM',
    message:
      'Sorry about that, the line dropped for a moment. Yes, you are eligible for a claim and I will email you the form right now.',
  },
];
